import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ApolloError } from '@apollo/client';
import { Helmet } from 'react-helmet-async';

import {
  Typography,
  Container,
  PageHeader,
  CircularProgress,
  ToolboxItemProps,
  Button,
  ToolboxContainer,
  ConfirmDialog,
  useAppLayoutContextConsumer,
  ArchiveIcon,
  EditIcon,
  CardContent,
  Card,
  ToolboxItem,
  NotFoundPage,
} from '@occc-app/design-system';
import { CampaignFragment } from '@occc/graphql';
import { useIsUserPermittedTo, Feature } from '@occc/utils-access-control';

import CampaignComponentsTable from './CampaignComponentsTable';
import { PageTitle } from './PageTitle';

import { getCampaignPageHeaderLinks, routeCreator } from '../utils';

interface CampaignDetailsPageProps {
  loading: boolean;
  error?: ApolloError;
  campaign?: CampaignFragment | null;
  toolboxItems: ToolboxItemProps[];
  handleCampaignDelete: () => void;
}

export const CampaignDetailsPage: React.FC<CampaignDetailsPageProps> = ({
  loading,
  campaign,
  error,
  toolboxItems,
  handleCampaignDelete,
}) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState<boolean>(false);
  const isUserPermittedToEditCampaign = useIsUserPermittedTo(Feature.CAMPAIGN_EDIT);
  const isUserPermittedToDeleteCampaign = useIsUserPermittedTo(Feature.CAMPAIGN_DELETE);
  const isUserPermittedToCreateCampaignComponents = useIsUserPermittedTo(
    Feature.CAMPAIGN_COMPONENT_CREATE,
  );

  const onConfirmButtonClick = () => {
    handleCampaignDelete();
    setDeleteDialogOpen(false);
  };

  const links = useMemo(
    () => getCampaignPageHeaderLinks(loading, !!error, campaign?.name),
    [campaign?.name, loading, error],
  );
  useAppLayoutContextConsumer(links);

  if (error) {
    return <Typography color='error'>Something went wrong. Please try again.</Typography>;
  }

  if (loading && !campaign) {
    return <CircularProgress />;
  }

  if (!campaign) {
    return <NotFoundPage />;
  }

  const isEditButtonShown = isUserPermittedToEditCampaign && campaign;
  const isArchiveButtonShown = isUserPermittedToDeleteCampaign && campaign && !campaign.deleted;
  const isToolboxSectionShown = isUserPermittedToCreateCampaignComponents && !campaign.deleted;

  return (
    <>
      <Helmet>
        <title>Campaign</title>
      </Helmet>
      <Container includesPageHeader>
        <PageHeader
          title={<PageTitle campaign={campaign} />}
          subtitle={campaign?.description || undefined}
        >
          {isEditButtonShown && (
            <Button
              color='secondary'
              variant='contained'
              disabled={campaign.deleted}
              component={Link}
              to={routeCreator.update(campaign?.uuid)}
              data-testid='campaign-init-edit-button'
              startIcon={<EditIcon />}
              role='button'
            >
              Edit
            </Button>
          )}
          {isArchiveButtonShown && (
            <Button
              color='secondary'
              variant='contained'
              component={Button}
              startIcon={<ArchiveIcon />}
              role='button'
              onClick={() => setDeleteDialogOpen(true)}
            >
              Archive
            </Button>
          )}
        </PageHeader>
        <>
          {isToolboxSectionShown && (
            <Card sx={{ mb: 3 }}>
              <CardContent>
                <ToolboxContainer>
                  {toolboxItems.map((item) => (
                    <ToolboxItem key={item.title} {...item} viewType='link' />
                  ))}
                </ToolboxContainer>
              </CardContent>
            </Card>
          )}
          <ConfirmDialog
            dialogTitle='Archive Campaign'
            open={deleteDialogOpen}
            onClose={() => setDeleteDialogOpen(false)}
            confirmButtonText='Archive'
            confirmButtonProps={{
              onClick: onConfirmButtonClick,
              color: 'error',
            }}
          >
            <Typography>
              {`Are you sure you would like to archive ${campaign.name} campaign? This will also archive all offers in the campaign.`}
            </Typography>
          </ConfirmDialog>
          <CampaignComponentsTable campaignId={campaign.uuid} />
        </>
      </Container>
    </>
  );
};
